/**
 * Save Smoke Drive files from the branded /files page and the desk.
 * Single files use a direct Storage link (no fetch), so bucket CORS is not required.
 * Folder zip / Duplicate / Print / Wave attach still read bytes in the
 * browser via download URL and need Storage CORS (repo root `storage-cors.json`).
 * In-pane PDF/image preview prefers Firebase Storage getBlob (no CORS required).
 */

import { buildZipStoreArchive } from './operatorDriveZip';
import { DRIVE_ZIP_MAX_BYTES } from './operatorDriveLibrary';
import { driveDownloadFileName, ensureDriveFileExtension } from './operatorDriveFileName';
import { setOperatorDriveAttachmentDisposition } from '../services/operatorDriveFileService';

/** Default operator hint (zip / generic fetch). */
export const DRIVE_STORAGE_CORS_MESSAGE =
    'The browser blocked reading this file from Storage (CORS). Use Download on a single file, or set Storage CORS on the bucket (repo root storage-cors.json — see AI_AGENT_SERVICE_GUIDE).';

/** Wave desk / Labels attach — prefer Upload PDF until CORS is set. */
export const DRIVE_STORAGE_CORS_WAVE_MESSAGE =
    'Smoke Drive attach needs Storage CORS on the bucket. Use Upload PDF for this wave until CORS is set (repo root storage-cors.json — see AI_AGENT_SERVICE_GUIDE).';

export const DRIVE_STORAGE_CORS_LABELS_MESSAGE =
    'Smoke Drive needs Storage CORS on the bucket. Use Upload PDF here until CORS is set (repo root storage-cors.json — see AI_AGENT_SERVICE_GUIDE).';

/**
 * @param {'default' | 'wave' | 'labels' | 'zip'} [context]
 * @param {unknown} [err]
 * @returns {string}
 */
export function formatDriveStorageCorsMessage(context, err) {
    const msg = String(err?.message || '');
    const looksCors =
        /CORS|storage-cors\.json/i.test(msg) ||
        msg === DRIVE_STORAGE_CORS_MESSAGE ||
        isLikelyCorsOrNetworkFetchError(err);
    if (!looksCors) {
        return msg || 'Could not load that Smoke Drive file.';
    }
    if (context === 'wave') return DRIVE_STORAGE_CORS_WAVE_MESSAGE;
    if (context === 'labels') return DRIVE_STORAGE_CORS_LABELS_MESSAGE;
    return DRIVE_STORAGE_CORS_MESSAGE;
}

/**
 * @param {unknown} err
 * @returns {boolean}
 */
export function isLikelyCorsOrNetworkFetchError(err) {
    if (!err) return false;
    const name = String(err.name || '');
    const message = String(err.message || '');
    if (name === 'TypeError') return true;
    return /failed to fetch|networkerror|load failed|cors/i.test(message);
}

/**
 * @param {string} fileName
 * @param {string} [contentType]
 * @returns {string}
 */
export function safeDownloadFileName(fileName, contentType) {
    const name = String(fileName || 'file')
        .replace(/[/\\?%*:|"<>]/g, '_')
        .trim() || 'file';
    return ensureDriveFileExtension(name, contentType);
}

/**
 * @param {Blob} blob
 * @param {string} fileName
 */
export function triggerBrowserFileSave(blob, fileName, contentType) {
    const name = safeDownloadFileName(fileName, contentType);
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
}

/**
 * Open a Storage download URL without fetch (avoids CORS). Call from a click.
 * @param {string} url
 * @param {string} [fileName]
 */
export function triggerBrowserUrlSave(url, fileName, contentType) {
    const href = String(url || '').trim();
    if (!href) throw new Error('This share has no file.');
    const named = safeDownloadFileName(fileName, contentType);
    const a = document.createElement('a');
    a.href = href;
    a.download = named;
    a.rel = 'noopener noreferrer';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
}

/**
 * @param {string} url
 * @param {string} [label]
 * @returns {Promise<Response>}
 */
export async function fetchDriveStorageUrl(url, label) {
    const href = String(url || '').trim();
    if (!href) throw new Error(`No download path for ${label || 'a file'}.`);
    let response;
    try {
        response = await fetch(href);
    } catch (err) {
        if (isLikelyCorsOrNetworkFetchError(err)) {
            throw new Error(DRIVE_STORAGE_CORS_MESSAGE);
        }
        throw err;
    }
    if (!response.ok) {
        throw new Error(
            `Could not download ${label || 'this file'}. The link may have been revoked.`
        );
    }
    return response;
}

/**
 * Load a Smoke Drive library item as a Blob for in-app use (print match, zip, etc.).
 * Uses the staff Storage download URL internally; callers must not paste that URL into print UI.
 *
 * @param {{
 *   id?: string,
 *   name?: string,
 *   originalFileName?: string,
 *   contentType?: string,
 *   downloadUrl?: string,
 *   sizeBytes?: number,
 * }} item
 * @param {{ resolveDownloadUrl?: (item: object) => Promise<string> }} [opts]
 * @returns {Promise<{ blob: Blob, fileName: string }>}
 */
export async function loadOperatorDriveItemBlob(item, opts = {}) {
    if (!item) throw new Error('No Smoke Drive file selected.');

    const storagePath = String(item.storagePath || '').trim();
    if (storagePath) {
        try {
            const { getOperatorDriveFileBlob } = await import(
                '../services/operatorDriveFileService'
            );
            const blob = await getOperatorDriveFileBlob(storagePath, item.contentType);
            const fileName = safeDownloadFileName(
                item.originalFileName || item.name,
                item.contentType || blob.type,
            );
            return { blob, fileName };
        } catch (sdkErr) {
            console.warn('[Smoke Drive] getBlob failed, trying download URL:', sdkErr?.code || sdkErr);
        }
    }

    let url = String(item.downloadUrl || '').trim();
    if (!url && typeof opts.resolveDownloadUrl === 'function') {
        url = String((await opts.resolveDownloadUrl(item)) || '').trim();
    }
    if (!url) throw new Error('No download path for that Smoke Drive file.');
    const response = await fetchDriveStorageUrl(url, item.name || 'file');
    const blob = await response.blob();
    if (!blob?.size) throw new Error('That Smoke Drive file is empty.');
    const fileName = safeDownloadFileName(
        item.originalFileName || item.name,
        item.contentType || blob.type,
    );
    const typed =
        blob.type && blob.type !== 'application/octet-stream'
            ? blob
            : new Blob([blob], {
                  type: item.contentType || blob.type || 'application/pdf',
              });
    return { blob: typed, fileName };
}

/**
 * @param {{
 *   downloadUrl: string,
 *   fileName?: string,
 *   originalFileName?: string,
 *   onProgress?: (pct: number) => void,
 * }} share
 */
export async function downloadDriveShareToDevice(share) {
    const url = String(share?.downloadUrl || '').trim();
    if (!url) throw new Error('This share has no file.');
    const fileName = driveDownloadFileName(share);
    if (share.storagePath) {
        await setOperatorDriveAttachmentDisposition(
            share.storagePath,
            fileName,
            share.contentType
        );
    }
    triggerBrowserUrlSave(url, fileName, share.contentType);
    if (typeof share.onProgress === 'function') share.onProgress(100);
}

/**
 * Download one file or zip a folder snapshot from the branded page.
 */
export async function downloadDriveSharePayload(share, onProgress) {
    const files = Array.isArray(share?.files) ? share.files.filter((f) => f?.downloadUrl) : [];
    const isFolder = share?.itemType === 'folder' || files.length > 1;
    if (isFolder) {
        if (!files.length) throw new Error('This folder share has no files.');
        const entries = [];
        for (let i = 0; i < files.length; i += 1) {
            const file = files[i];
            const response = await fetchDriveStorageUrl(
                file.downloadUrl,
                file.fileName || 'a file'
            );
            const bytes = new Uint8Array(await response.arrayBuffer());
            entries.push({
                name: driveDownloadFileName(file),
                bytes,
            });
            if (onProgress) {
                onProgress(Math.min(99, Math.round(((i + 1) / files.length) * 100)));
            }
        }
        const totalBytes = entries.reduce((sum, entry) => sum + entry.bytes.length, 0);
        if (totalBytes > DRIVE_ZIP_MAX_BYTES) {
            throw new Error('This folder is larger than the 400 MB zip cap.');
        }
        const zipName = `${String(share.fileName || 'folder').replace(/\.zip$/i, '')}.zip`;
        triggerBrowserFileSave(buildZipStoreArchive(entries), zipName);
        if (onProgress) onProgress(100);
        return;
    }
    await downloadDriveShareToDevice({ ...share, onProgress });
}

/**
 * Zip selected Smoke Drive files inside the desk (staff Storage URLs).
 * @param {object[]} items
 * @param {{ onProgress?: (pct: number) => void, zipName?: string, resolveUrl?: (item: object) => Promise<string> }} [opts]
 */
export async function zipDriveItemsToDevice(items, opts = {}) {
    const list = (Array.isArray(items) ? items : []).filter((item) => item && item.type !== 'folder');
    if (!list.length) throw new Error('Select one or more files to zip.');
    const totalHint = list.reduce((sum, item) => sum + (Number(item.sizeBytes) || 0), 0);
    if (totalHint > DRIVE_ZIP_MAX_BYTES) {
        throw new Error('Selection is larger than the 400 MB zip cap.');
    }
    const entries = [];
    for (let i = 0; i < list.length; i += 1) {
        const item = list[i];
        let url = String(item.downloadUrl || '').trim();
        if (!url && typeof opts.resolveUrl === 'function') {
            url = String((await opts.resolveUrl(item)) || '').trim();
        }
        const response = await fetchDriveStorageUrl(url, item.name || 'a file');
        const bytes = new Uint8Array(await response.arrayBuffer());
        entries.push({
            name: driveDownloadFileName(item),
            bytes,
        });
        if (typeof opts.onProgress === 'function') {
            opts.onProgress(Math.min(99, Math.round(((i + 1) / list.length) * 100)));
        }
    }
    const zipName = String(opts.zipName || 'Smoke Drive files.zip');
    triggerBrowserFileSave(buildZipStoreArchive(entries), zipName);
    if (typeof opts.onProgress === 'function') opts.onProgress(100);
}
